import React, { useEffect, useMemo, useState } from 'react';
import { Card, Box, Typography, Skeleton } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Icon } from '@iconify/react';

export default function BestTeamCard({ data, loading, onRefresh, refreshing, lastUpdated }) {
  const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;

  const bestTeam = data?.data?.bestTeam || {};
  const revenueTrend = bestTeam?.trends?.revenue;
  const admissionTrend = bestTeam?.trends?.admissions;

  const noData = !loading && (!bestTeam || Object.keys(bestTeam).length === 0);

  return (
    <Card
      sx={{
        width: '100%',
        p: 3,
        borderRadius: 2,
        backgroundColor: '#f5ecd7',
        border: '2px solid #f4b400',
      }}
    >
      {/* Header */}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" fontWeight="bold">
          Best Team <span style={{ color: 'red' }}>(Live)</span>
        </Typography>
        <Typography fontSize={12} color="gray">
          Updated {lastUpdated}
        </Typography>
        <Icon
          icon="mdi:refresh"
          width="20"
          height="20"
          style={{
            cursor: 'pointer',
            opacity: refreshing ? 0.5 : 1, // 👈 Visual feedback
            transform: refreshing ? 'rotate(180deg)' : 'none',
            transition: 'all 0.3s ease',
          }}
          onClick={onRefresh}
        />
      </Box>

      {loading && !refreshing ? (
        <Box mt={2}>
          {/* Top Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={50} height={50} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={30} />
              <Skeleton width="80%" />
            </Box>
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Skeleton variant="rounded" height={50} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={50} sx={{ flex: 1 }} />
          </Box>

          {/* Growth */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Skeleton width={40} />
            <Skeleton width={40} />
          </Box>
        </Box>
      ) : noData ? (
        <Box
          sx={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="text.secondary">No Data Found</Typography>
        </Box>
      ) : (
        <>
          {/* Team Info */}

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Icon icon="lucide:trophy" width="50" height="50" style={{ color: '#c88700', marginRight: '16px' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {bestTeam?.name}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon icon="f7:person-2" width="20" height="20" style={{ color: 'green' }} />
                <Box component="span" sx={{ color: 'green' }}>
                  {bestTeam?.admissions}
                </Box>
                - Revenue:
                <Box component="span" sx={{ color: 'green', fontWeight: 'bold' }}>
                  {bestTeam?.revenue}
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* Stats Boxes */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {/* Revenue */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: '#f1f1e4',
                p: 1.5,
                borderRadius: 2,
              }}
            >
              <Typography fontWeight="bold" sx={{ color: 'green' }}>
                {formatCurrency(bestTeam?.revenue)}
              </Typography>
            </Box>

            {/* Members */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: '#f1f1e4',
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PeopleIcon fontSize="small" sx={{ color: 'green' }} />
              <Typography fontWeight="bold" sx={{ color: 'green' }}>
                {bestTeam?.admissions}
              </Typography>
              <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
            </Box>
          </Box>

          {/* Growth */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'green', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon icon="lucide:arrow-up" width="14" height="14" />
              {revenueTrend}
            </Typography>
            <Typography variant="caption" sx={{ color: 'green', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
              {admissionTrend}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
}
